import React, { useEffect, useMemo, useState } from 'react';
import { Guide } from '@/pages/home/components/Guide';
import {
  getLocale,
  useDispatch,
  useIntl,
  useLocation,
  useSelector,
  history,
} from 'umi';

import {
  OPINION_GUIDE_BOX_LIST4,
  OPINION_GUIDE_HIDE,
} from '@/pages/home/components/GuideModule/GuideOpinionBox';
import {
  GUIDE_TYPE_OPINION_COMMUNITY,
  GUIDE_SYS_OPINION,
} from '@/pages/home/models/guide';
import { storage } from '@/utils/utils';

const STEP1_PATH = '/opinion/Community/Video';
const STEP2_PATH = '/opinion/Community/Streaming';
const STEP3_PATH = '/opinion/Influencer/BrandCreatorList';
const STEP_PATH_LIST = [STEP1_PATH, STEP2_PATH, STEP3_PATH];

import './index.scss';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import useOpinionGuideData from '@home/components/GuideModule/GuideOpinionBox/useOpinionGuideData';
const GuideOverView = () => {
  const location = useLocation();
  const { pathname, query } = location;
  const { from } = query || {};
  const [currentGuideStep, setCurrentGuideStep] = useState(-3);
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isEn = locale === 'en-US';
  const dispatch = useDispatch();
  const { guideType, userInfo, gameInfo } = useSelector((state: any) => {
    const {
      guide: { guideType },
      home: { userInfo },
      opinionCommon: { gameInfo },
    } = state;
    return {
      guideType,
      userInfo,
      gameInfo,
    };
  });
  const { excludeGuideSource } = useGetSingePageSourceRule({});
  const { GuideData } = useOpinionGuideData({
    gameInfo,
    userInfo,
    guidePermissions: excludeGuideSource,
  });

  const guideUsersHide = storage.getLocalStorageItem(OPINION_GUIDE_HIDE);

  // 注意下面两个useEffect不要合并到一起，不然会出现：从头部导航进入新手引导，点击到下一步时，关闭按钮无法关闭，而是返回到第一步
  useEffect(() => {
    if (guideType === GUIDE_TYPE_OPINION_COMMUNITY) {
      if (
        pathname !== STEP1_PATH &&
        from !== 'userGuid_1' &&
        from !== 'userGuid_2'
      ) {
        return;
      }

      setCurrentGuideStep(
        from === 'userGuid_2' ? 2 : from === 'userGuid_1' ? 1 : 0,
      );
    }
  }, [guideType]);

  useEffect(() => {
    const list = storage.getLocalStorageItem(OPINION_GUIDE_BOX_LIST4) || [];
    const guideHide = storage.getLocalStorageItem(OPINION_GUIDE_HIDE);
    if (userInfo?.openid && !list.includes(userInfo.openid) && !guideHide) {
      if (
        pathname !== STEP1_PATH &&
        from !== 'userGuid_1' &&
        from !== 'userGuid_2'
      ) {
        return;
      }
      setCurrentGuideStep(
        from === 'userGuid_2' ? 2 : from === 'userGuid_1' ? 1 : 0,
      );
    }
  }, [userInfo]);

  const onFinished = () => {
    if (userInfo.openid) {
      const list = storage.getLocalStorageItem(OPINION_GUIDE_BOX_LIST4) || [];
      !list?.includes(userInfo.openid) && list.push(userInfo.openid);
      storage.setLocalStorageItem(OPINION_GUIDE_BOX_LIST4, list);
    }
    dispatch({
      type: 'guide/showOpinionGuideModule',
      payload: true,
    });
  };

  const onCloseStep = () => {
    dispatch({
      type: 'guide/save',
      payload: {
        guideSys: GUIDE_SYS_OPINION,
        guideType: '',
      },
    });
  };

  const onStepChange = (current: number) => {
    const { pathname } = location;
    if (current >= 0 && pathname !== STEP_PATH_LIST[current]) {
      history.push(`${STEP_PATH_LIST[current]}?from=userGuid_${current}`);
    }
    setCurrentGuideStep(current);
  };

  const subtitle = formatMessage({
    id: 'Community Performance Monitoring: Video, Streaming, and Influencers',
  });
  const { blockList } = GuideData;
  const rank = blockList.findIndex((item) => item.tit === subtitle) + 1;
  const title = `${formatMessage({ id: 'Core Function' })} ${rank}`;
  // const rank = gameInfo?.entity_type !== 'mobile' ? 3 : 4;

  const steps = useMemo(() => {
    const steps = [
      {
        element: () => document.querySelector('.user-guide-video-mask'),
        title,
        subtitle,
        description: formatMessage({
          id: 'Get the engagement metrics of the most popular videos and the number of followers the video creator has.',
        }),
        placement: 'top',
      },
      {
        element: () => document.querySelector('.segment-light'),
        title,
        subtitle,
        description: formatMessage({
          id: 'Click these metrics to view multi-dimensional live streaming data.',
        }),
        placement: 'bottom',
        padding: { paddingLeft: 5, paddingRight: 5 },
      },
      {
        element: () => {
          return document.querySelector('.user-guide-influencer-mask');
        },
        title,
        subtitle,
        description: formatMessage({
          id: 'By sorting, get information on the hottest and fastest growing internet influencers.',
        }),
        placement: 'top',
      },
    ];

    return steps;
  }, [currentGuideStep, locale]);

  if (guideType !== GUIDE_TYPE_OPINION_COMMUNITY) return null;

  return (
    // finishText 根据中英文调整
    <Guide
      current={currentGuideStep}
      onCurrentChange={(
        current: number,
        context: {
          from: 'start' | 'back' | 'next' | 'finish' | 'cancel' | 'nodata';
        },
      ) => {
        const { from } = context;
        setCurrentGuideStep(current);
        onStepChange(current);
        if (from === 'finish' || from === 'nodata') {
          onFinished();
        } else if (from === 'cancel') {
          onCloseStep();
          onFinished();
        }
      }}
      visibleCloseBtn
      hideCancelButton
      showBackButton
      nextButtonTheme="primary"
      backText={formatMessage({ id: 'guide_last_step' })}
      nextText={formatMessage({ id: 'guide_next_step' })}
      finishText={formatMessage({ id: 'guide_more_step' })}
      steps={steps}
      showLoading={false}
    />
  );
};

export default () => {
  const location = useLocation();
  return <>{STEP_PATH_LIST.includes(location.pathname) && <GuideOverView />}</>;
};
